import * as React from 'react';

export interface NotificationOptions {
  severity?: 'info' | 'warning' | 'error' | 'success';
  autoHideDuration?: number;
}

interface NotificationContextType {
  show: (message: React.ReactNode, options?: NotificationOptions) => void;
}

const NotificationContext = React.createContext<NotificationContextType | null>(null);

export const useNotification = () => {
  const context = React.useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};

interface NotificationState {
  message: React.ReactNode;
  severity: 'info' | 'warning' | 'error' | 'success';
  isVisible: boolean;
  isClosing: boolean;
}

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notification, setNotification] = React.useState<NotificationState>({
    message: '',
    severity: 'info',
    isVisible: false,
    isClosing: false,
  });

  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const closeTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const show = React.useCallback((message: React.ReactNode, options?: NotificationOptions) => {
    // Clear any existing timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }

    const severity = options?.severity || 'info';
    const autoHideDuration = options?.autoHideDuration || 3000;

    setNotification({
      message,
      severity,
      isVisible: true,
      isClosing: false,
    });

    // Auto-hide after duration
    timeoutRef.current = setTimeout(() => {
      setNotification((prev) => ({ ...prev, isClosing: true }));
      closeTimeoutRef.current = setTimeout(() => {
        setNotification((prev) => ({ ...prev, isVisible: false, isClosing: false }));
      }, 300);
    }, autoHideDuration);
  }, []);

  const contextValue = React.useMemo(() => ({ show }), [show]);

  const getSeverityColor = () => {
    switch (notification.severity) {
      case 'success':
        return 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
      case 'error':
        return 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
      case 'warning':
        return 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';
      case 'info':
      default:
        return 'linear-gradient(135deg, #028bcb 0%, #016a96 100%)';
    }
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      {notification.isVisible && (
        <div
          className={notification.isClosing ? 'custom-notification closing' : 'custom-notification'}
          style={{
            position: 'fixed',
            bottom: '24px',
            left: '24px',
            zIndex: 9999,
            minWidth: '320px',
            maxWidth: '500px',
            padding: '16px 24px',
            background: getSeverityColor(),
            color: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
            backdropFilter: 'blur(10px)',
            fontSize: '15px',
            fontWeight: 500,
            letterSpacing: '0.3px',
            lineHeight: '1.5',
            animation: notification.isClosing ? 'slideOut 0.3s ease-out forwards' : 'slideIn 0.3s ease-out',
          }}
        >
          {notification.message}
        </div>
      )}
      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(-100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes slideOut {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(-100%);
            opacity: 0;
          }
        }
      `}</style>
    </NotificationContext.Provider>
  );
};

